import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingConfirmations } from '../../contexts/PendingConfirmationsContext';
import { 
    LayoutDashboard, 
    Truck, 
    Package, 
    Users, 
    FileText, 
    MapPin, 
    DollarSign, 
    Settings,
    LogOut,
    Menu,
    X,
    Search,
    Bell,
    ChevronLeft,
    ChevronRight,
    Navigation,
    Building2,
    Clock,
    Gauge,
    Receipt,
    AlertTriangle,
    FilePen,
    Shield,
    Bot,
    Target,
    Briefcase,
    Edit3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { apiFetch } from '../../lib/api';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEV_BUILD_BADGE = 'DEV 7f53c7c';

const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/command-center', icon: Target, label: 'Command Center' },
    { path: '/operations', icon: Briefcase, label: 'Operations', hasBadge: true },
    { path: '/loads', icon: Package, label: 'My Loads' },
    { path: '/staged-loads', icon: Clock, label: 'Pending', hasBadge: true },
    { path: '/rate-cons', icon: FilePen, label: 'Rate Cons' },
    { path: '/trucks', icon: Truck, label: 'Trucks' },
    { path: '/carriers', icon: Shield, label: 'Carriers' },
    { path: '/hos', icon: Gauge, label: 'HOS Command' },
    { path: '/brokers', icon: Building2, label: 'Brokers' },
    { path: '/receivables', icon: Receipt, label: 'Receivables' },
    { path: '/tracking', icon: Navigation, label: 'GPS Tracking' },
    { path: '/documents', icon: FileText, label: 'Documents' },
    { path: '/routes', icon: MapPin, label: 'Routes' },
    { path: '/financials', icon: DollarSign, label: 'Financials' },
    { path: '/office-desk', icon: Edit3, label: 'Office Desk' },
    { path: '/ai-settings', icon: Bot, label: 'AI Models' },
    { path: '/settings', icon: Settings, label: 'Settings' },
];

export const AppLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const { count: pendingCount } = usePendingConfirmations();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [alertCount, setAlertCount] = useState(0);
    const [systemMode, setSystemMode] = useState(null);

    // Fetch alert count
    const fetchAlertCount = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/alerts/count`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setAlertCount(data.total || 0);
            }
        } catch (error) {
            // Silently fail - alerts are optional
        }
    }, []);

    // Fetch system settings to check mode
    const fetchSystemMode = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/settings`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setSystemMode(data.system_mode || 'production');
            }
        } catch (error) {
            // Silently fail - default to production
            setSystemMode('production');
        }
    }, []);

    // Listen for system mode changes from Settings page
    useEffect(() => {
        const handleModeChange = (event) => {
            if (event.detail && event.detail.system_mode) {
                setSystemMode(event.detail.system_mode);
            }
        };
        window.addEventListener('systemModeChanged', handleModeChange);
        return () => window.removeEventListener('systemModeChanged', handleModeChange);
    }, []);

    useEffect(() => {
        fetchAlertCount();
        fetchSystemMode();
        const alertInterval = setInterval(fetchAlertCount, 60000); // Refresh every minute
        const modeInterval = setInterval(fetchSystemMode, 30000); // Check mode every 30 seconds
        return () => {
            clearInterval(alertInterval);
            clearInterval(modeInterval);
        };
    }, [fetchAlertCount, fetchSystemMode]);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Badge color logic: 0 → no badge, 1-3 → amber, 4+ → red
    const getBadgeColor = (count) => {
        if (count === 0) return null;
        if (count <= 3) return 'bg-amber-500';
        return 'bg-red-500';
    };

    // Alert badge color: always red for critical alerts visibility
    const getAlertBadgeColor = (count) => {
        if (count === 0) return null;
        return 'bg-red-500';
    };

    return (
        <div className="app-shell">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div 
                    className="sidebar-overlay md:hidden"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="sidebar-overlay"
                />
            )}

            {/* Sidebar */}
            <aside 
                className={`sidebar ${sidebarOpen ? 'open' : ''} ${collapsed ? 'sidebar-collapsed' : ''}`}
                data-testid="app-sidebar"
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
                    {!collapsed && (
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-orange-500 rounded-sm flex items-center justify-center">
                                <Truck className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-bold text-white tracking-tight">LoadTMS</span>
                        </div>
                    )}
                    {collapsed && (
                        <div className="w-8 h-8 bg-orange-500 rounded-sm flex items-center justify-center mx-auto">
                            <Truck className="w-5 h-5 text-white" />
                        </div>
                    )}
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden text-zinc-400 hover:text-white"
                        data-testid="close-sidebar-btn"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''} relative`
                            }
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                            <div className="relative">
                                <item.icon className="w-5 h-5 flex-shrink-0" />
                                {/* Badge for collapsed sidebar - pending confirmations */}
                                {collapsed && item.hasBadge && pendingCount > 0 && (
                                    <span 
                                        className={`absolute -top-1 -right-1 w-4 h-4 ${getBadgeColor(pendingCount)} text-white text-[10px] font-bold rounded-full flex items-center justify-center`}
                                        data-testid="pending-badge-collapsed"
                                    >
                                        {pendingCount > 9 ? '9+' : pendingCount}
                                    </span>
                                )}
                                {/* Badge for collapsed sidebar - alerts */}
                                {collapsed && item.hasAlertBadge && alertCount > 0 && (
                                    <span 
                                        className={`absolute -top-1 -right-1 w-4 h-4 ${getAlertBadgeColor(alertCount)} text-white text-[10px] font-bold rounded-full flex items-center justify-center`}
                                        data-testid="alert-badge-collapsed"
                                    >
                                        {alertCount > 9 ? '9+' : alertCount}
                                    </span>
                                )}
                            </div>
                            {!collapsed && (
                                <span className="text-sm font-medium flex-1">{item.label}</span>
                            )}
                            {/* Badge for expanded sidebar - pending confirmations */}
                            {!collapsed && item.hasBadge && pendingCount > 0 && (
                                <span 
                                    className={`${getBadgeColor(pendingCount)} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center`}
                                    data-testid="pending-badge"
                                >
                                    {pendingCount}
                                </span>
                            )}
                            {/* Badge for expanded sidebar - alerts */}
                            {!collapsed && item.hasAlertBadge && alertCount > 0 && (
                                <span 
                                    className={`${getAlertBadgeColor(alertCount)} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center`}
                                    data-testid="alert-badge"
                                >
                                    {alertCount}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Collapse toggle (desktop only) */}
                <div className="hidden md:flex p-2 border-t border-zinc-800">
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="w-full flex items-center justify-center py-2 text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-sm transition-colors"
                        data-testid="collapse-sidebar-btn"
                    >
                        {collapsed ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <>
                                <ChevronLeft className="w-5 h-5 mr-2" />
                                <span className="text-sm">Collapse</span>
                            </>
                        )}
                    </button>
                </div>

                {/* User section */}
                {!collapsed && (
                    <div className="p-4 border-t border-zinc-800">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                                <AvatarImage src={user?.picture} alt={user?.name} />
                                <AvatarFallback className="bg-zinc-800 text-orange-500">
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                                <p className="text-xs text-zinc-500 truncate">{user?.role || 'Dispatcher'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </aside>

            {/* Main content area */}
            <div className={`main-content ${collapsed ? 'main-content-collapsed' : ''}`}>
                {/* Simulation Mode Banner */}
                {systemMode === 'testing' && (
                    <div 
                        className="bg-red-600 text-white text-center py-2 px-4 font-bold text-sm tracking-wide flex items-center justify-center gap-2"
                        data-testid="simulation-mode-banner"
                    >
                        <AlertTriangle className="w-4 h-4" />
                        SIMULATION MODE – No real integrations active
                        <AlertTriangle className="w-4 h-4" />
                    </div>
                )}

                {/* Header */}
                <header className="app-header" data-testid="app-header">
                    <div
                        className="fixed bottom-2 right-2 z-[9999] px-2 py-1 rounded bg-zinc-950/90 border border-orange-500/60 text-orange-400 text-[10px] font-mono tracking-wide shadow-lg"
                        data-testid="dev-build-badge"
                        title="Current deployed frontend build"
                    >
                        {DEV_BUILD_BADGE}
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Mobile menu button */}
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="md:hidden text-zinc-400 hover:text-white"
                            data-testid="open-sidebar-btn"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        {/* Search */}
                        <div className="hidden sm:flex items-center relative">
                            <Search className="w-4 h-4 text-zinc-500 absolute left-3" />
                            <Input
                                placeholder="Search loads, carriers..."
                                className="w-64 pl-9 bg-zinc-900 border-zinc-800 focus:border-orange-500"
                                data-testid="global-search-input"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Notifications */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-zinc-400 hover:text-white"
                            data-testid="notifications-btn"
                        >
                            <Bell className="w-5 h-5" />
                        </Button>

                        {/* User dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    className="flex items-center gap-2 px-2"
                                    data-testid="user-dropdown-trigger"
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={user?.picture} alt={user?.name} />
                                        <AvatarFallback className="bg-zinc-800 text-orange-500 text-xs">
                                            {getInitials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="hidden md:inline text-sm text-zinc-300">
                                        {user?.name?.split(' ')[0]}
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                                <DropdownMenuLabel className="text-zinc-400">My Account</DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-zinc-800" />
                                <DropdownMenuItem 
                                    onClick={() => navigate('/settings')}
                                    className="cursor-pointer hover:bg-zinc-800"
                                    data-testid="settings-dropdown-item"
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    Settings
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={handleLogout}
                                    className="cursor-pointer text-red-400 hover:bg-zinc-800 hover:text-red-400"
                                    data-testid="logout-dropdown-item"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Logout
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page content */}
                <main className="page-content" data-testid="page-content">
                    {children}
                </main>
            </div>
        </div>
    );
};
